import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useAuth } from '../../lib/auth';
import { ResonanceMark } from '../../components/ui/ResonanceMark';
import { PublicAppearanceSelector } from '../../components/ui/PublicAppearanceSelector';
import styles from './Public.module.css';

export function SignInPage() {
  const { signInWithGoogle, signInWithPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signInWithPassword({ email, password });
      void navigate({ to: '/app' });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  const google = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Google sign-in is unavailable.');
      setBusy(false);
    }
  };

  return (
    <div className={styles.screen}>
      <div className="appGradient" />
      <div className={styles.appearanceSelector}>
        <PublicAppearanceSelector />
      </div>
      <div className={styles.card}>
        <div className={styles.brand}>
          <ResonanceMark size={26} className={styles.mark} />
          Resonance
        </div>
        <p className={styles.subtitle}>Sign in to your workspace.</p>

        {error ? <p className={styles.error}>{error}</p> : null}

        <button type="button" className={styles.secondary} onClick={() => void google()} disabled={busy}>
          Continue with Google
        </button>

        <div className={styles.divider}>or</div>

        <form className={styles.form} onSubmit={(event) => void submit(event)}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className={styles.input}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className={styles.input}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={10}
            />
          </div>
          <button type="submit" className={styles.primary} disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
